import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  Table,
  Tr,
  Td,
  Th,
  TdImp
} from '../fmComponents/fmMicrosoftLicensing/css';
import EN from '../../../EN.json';
import { formatCurrency } from '../../utils/currencyFormatter';

const FMProducts = ({ orderData }) => {
  const {
    srtDashboardFM: { managedItServicesFM, microsoftLicensingFM }
  } = EN;
  const {
    productSelectionFM,
    microsoftLicensingData: {
      selections: microsoftlicensingSelections = []
    } = {},
    managedItServicesSummary: {
      additionalCharges = [],
      charges: managedItServices = [],
      totalMonthlyCharges = 0
    } = {},
    discountValue,
    approverEmail,
    approverName,
    dateApproved
  } = orderData;
  const totals = useMemo(() => {
    let totalCostSum = 0;
    let totalPriceSum = 0;
    microsoftlicensingSelections.forEach(item => {
      totalCostSum += item.totalCost;
      totalPriceSum += item.totalPrice;
    });
    return {
      totalCost: totalCostSum,
      totalPrice: totalPriceSum
    };
  }, [microsoftlicensingSelections]);
  const itServicesTotal = useMemo(() => {
    let additionalChargesSum = 0;
    let managedItChargesSum = 0;
    additionalCharges.forEach(item => {
      additionalChargesSum += item.total;
    });
    managedItServices.forEach(item => {
      managedItChargesSum += item.total;
    });
    return {
      totalAdditionalCharges: additionalChargesSum,
      totalManagedItCharges: managedItChargesSum
    };
  }, [additionalCharges, managedItServices]);
  const additionalServicesTotalMonthlyCharges = useMemo(() => {
    const additionalServices = managedItServices.filter(item =>
      item.name.includes('Additional Service')
    );
    return additionalServices.reduce(
      (total, service) => total + service.total,
      0
    );
  }, [managedItServices]);

  // const managedServicesTotalMonthlyCharges = useMemo(() => {
  //   const managedServices = managedItServices.filter(
  //     item => !item.name.includes('Additional Service')
  //   );
  //   return managedServices.reduce((total, service) => total + service.total, 0);
  // }, [managedItServices]);

  const totalMonthlyChargesWithoutAdditionalServices =
    totalMonthlyCharges - additionalServicesTotalMonthlyCharges;

  const grandTotal =
    totalMonthlyChargesWithoutAdditionalServices +
    additionalServicesTotalMonthlyCharges +
    (totals ? totals.totalPrice : 0) -
    (discountValue ? parseFloat(discountValue) : 0);

  return (
    <div>
      {productSelectionFM.includes(managedItServicesFM[0]) && (
        <>
          {managedItServices && managedItServices.length && (
            <Table>
              <Th>Managed IT Services</Th>
              <Th>Qty</Th>
              {managedItServices
                .filter(item => !item.name.includes('Additional Service'))
                .map(({ name, quantity }) => (
                  <Tr key={name}>
                    <Td>{name}</Td>
                    <Td>{quantity}</Td>
                  </Tr>
                ))}
              <Tr>
                <TdImp colSpan={1}>Total Monthly Charges</TdImp>
                <TdImp>
                  {formatCurrency(totalMonthlyChargesWithoutAdditionalServices)}
                </TdImp>
              </Tr>
            </Table>
          )}
          {productSelectionFM.includes(microsoftLicensingFM[0]) && (
            <>
              {!!microsoftlicensingSelections.length && (
                <Table>
                  <Th>Microsoft Licensing Subscriptions</Th>
                  <Th>Cost</Th>
                  <Th>Price Per Unit</Th>
                  <Th>Qty</Th>
                  <Th>Total Charge Per Month For 1 Year</Th>
                  {microsoftlicensingSelections.map(
                    ({
                      subscriptionLineOption,
                      cost,
                      price,
                      qtyOfLicenses,
                      totalPrice
                    }) => (
                      <Tr key={subscriptionLineOption}>
                        <Td>{subscriptionLineOption}</Td>
                        <Td>{formatCurrency(cost)}</Td>
                        <Td>{formatCurrency(price)}</Td>
                        <Td>{qtyOfLicenses}</Td>
                        <Td>{formatCurrency(totalPrice)}</Td>
                      </Tr>
                    )
                  )}
                  <Tr>
                    <TdImp colSpan={4}>Total Monthly Charges</TdImp>
                    <TdImp>{formatCurrency(totals.totalPrice)}</TdImp>
                  </Tr>
                </Table>
              )}
            </>
          )}
          {managedItServices &&
            managedItServices.length &&
            managedItServices.some(item =>
              item.name.includes('Additional Service')
            ) && (
              <Table>
                <Th>Additional Services</Th>
                <Th>Qty</Th>
                <Th>Monthly Charges</Th>
                {managedItServices
                  .filter(item => item.name.includes('Additional Service'))
                  .map(({ name, quantity, total }) => (
                    <Tr key={name}>
                      <Td>{name}</Td>
                      <Td>{quantity}</Td>
                      <Td>{formatCurrency(total)}</Td>
                    </Tr>
                  ))}
                <Tr>
                  <TdImp colSpan={2}>Monthly Additional Services</TdImp>
                  <TdImp>
                    {formatCurrency(additionalServicesTotalMonthlyCharges)}
                  </TdImp>
                </Tr>
              </Table>
            )}
          {additionalCharges && additionalCharges.length && (
            <Table>
              <Th>Additional Charges</Th>
              <Th>One Time Charges</Th>
              {additionalCharges.map(({ name, total }) => (
                <Tr key={name}>
                  <Td>{name}</Td>
                  <Td>{formatCurrency(total)}</Td>
                </Tr>
              ))}
              <Tr>
                <TdImp colSpan={1}>Total One Time Charges</TdImp>
                <TdImp>
                  {formatCurrency(itServicesTotal.totalAdditionalCharges)}
                </TdImp>
              </Tr>
            </Table>
          )}
        </>
      )}
      {discountValue && (
        <Table>
          <Th>Discount</Th>
          <Th>Total Discount Per month for 1 year</Th>
          <Tr>
            <TdImp>Total Monthly Discount</TdImp>
            <TdImp>${discountValue}</TdImp>
          </Tr>
          <Tr>
            <TdImp>Approver Name</TdImp>
            <TdImp>{approverName}</TdImp>
          </Tr>
          <Tr>
            <TdImp>Approver Email</TdImp>
            <TdImp>{approverEmail}</TdImp>
          </Tr>
          <Tr>
            <TdImp>Approved Date</TdImp>
            <TdImp>{dateApproved}</TdImp>
          </Tr>
        </Table>
      )}
      <Table>
        <Th>Order Summary</Th>
        <Th>Total Charges</Th>
        {managedItServices && managedItServices.length && (
          <Tr>
            <TdImp>Monthly Managed IT Services</TdImp>
            <TdImp>
              {formatCurrency(totalMonthlyChargesWithoutAdditionalServices)}
            </TdImp>
          </Tr>
        )}
        {productSelectionFM.includes(microsoftLicensingFM[0]) && (
          <>
            {!!microsoftlicensingSelections.length && (
              <Tr>
                <TdImp>Monthly Microsoft Licensing</TdImp>
                <TdImp>{formatCurrency(totals.totalPrice)}</TdImp>
              </Tr>
            )}
          </>
        )}
        {managedItServices &&
          managedItServices.length &&
          managedItServices.some(item =>
            item.name.includes('Additional Service')
          ) && (
            <Tr>
              <TdImp>Monthly Additional Services</TdImp>
              <TdImp>
                {formatCurrency(additionalServicesTotalMonthlyCharges)}
              </TdImp>
            </Tr>
          )}
        {discountValue && (
          <Tr>
            <TdImp>Monthly Discount</TdImp>
            <TdImp>-${discountValue}</TdImp>
          </Tr>
        )}
        <Tr>
          <TdImp>Total Monthly Charges (CAD)</TdImp>
          <TdImp>{formatCurrency(grandTotal)}</TdImp>
        </Tr>
        <Tr>
          <TdImp>Total One Time Charges (CAD)</TdImp>
          <TdImp>
            {formatCurrency(itServicesTotal.totalAdditionalCharges)}
          </TdImp>
        </Tr>
      </Table>
      {orderData && orderData.remediationProject && orderData.remediationProject.toUpperCase() === 'YES' && (          
            <p style={{marginTop: '10px'}}>
              <strong style={{fontWeight: 600}}>
                Estimated Remediation Project ({orderData.remediationCurrency}):</strong> $
                {orderData.remediationMonthlyCharge ? orderData.remediationMonthlyCharge : orderData.remediationOneTimeCharge}
                {' '}
                ({orderData.remediationMonthlyCharge ? 'Monthly' : 'One Time'})
              
            </p>
        )}
    </div>
  );
};

FMProducts.propTypes = {
  orderData: PropTypes.shape({
    productSelectionFM: PropTypes.arrayOf(PropTypes.string),
    microsoftLicensingData: {
      selections: PropTypes.array
    },
    managedItServicesSummary: {
      additionalCharges: PropTypes.array,
      charges: PropTypes.array
    },
    discountValue: PropTypes.string,
    approverEmail: PropTypes.string,
    approverName: PropTypes.string,
    dateApproved: PropTypes.string
  }).isRequired
};

export default FMProducts;
